import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, MessageSquareIcon, MusicIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/ec175633-6df8-4ed9-ae67-c7c09deb78ea/1792723062269_pr-mockup-landing-9c446ac2.webp';
export const heroGuarantees = ['Instant digital download', 'One-time payment', 'No subscription', '7-day money-back guarantee'];
export const stats = [{
  value: '240',
  label: 'Pages in the main volumes'
}, {
  value: '6',
  label: 'Digital volumes'
}, {
  value: '5+',
  label: 'Ages 5 to adult'
}, {
  value: '5',
  label: 'Bonuses included'
}, {
  value: '3',
  label: 'Worksheet levels'
}];
export const differenceStats = [{
  value: 'Ages 5 to adult',
  label: 'Young beginners, older beginners, teens & adults'
}, {
  value: '6 digital volumes',
  label: 'From your studio year plan and method to theory, repertoire and assessment'
}, {
  value: 'Print-ready PDF',
  label: 'Use it on your tablet or print it for the piano bench'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Sunday nights disappear',
  text: 'Hours at the computer planning next week’s lessons – and you’re still not done.'
}, {
  title: '“What do I teach this week?”',
  text: 'Every student is at a different level, and you rebuild a plan for each one from scratch.'
}, {
  title: 'Students who don’t practice',
  text: 'They come back each week with the same piece, the same mistakes and no clear next step.'
}, {
  title: 'Worksheets from everywhere',
  text: 'Free printables from ten different sites – too easy, too hard, or not in the order you teach.'
}, {
  title: 'Parents asking about progress',
  text: 'You know your students are growing, but it’s hard to show it clearly on paper.'
}, {
  title: 'Improvising at the bench',
  text: 'When there’s no time to prepare, the lesson turns into “let’s just play it again.”'
}];
export const afterItems = ['A 36-week studio year plan for 3 age tracks', '12 lessons planned minute by minute (30 / 45 / 60 min)', 'Materials list and teacher script for every lesson', 'Worksheets in 3 levels with answer keys', 'A leveled method from Pre-Staff to Late Elementary', 'Note reading, rhythm and theory step by step', 'Public-domain repertoire guide by level', 'Progress reports, rubrics and 100 ready-made comments', 'Print-ready PDF'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Your evenings go to lesson planning',
  text: 'Do you spend nights and weekends figuring out what each student should work on next?'
}, {
  icon: FolderSearchIcon,
  title: 'You never know which material to use',
  text: 'Are you always searching online for worksheets and pieces that fit each student’s level?'
}, {
  icon: UsersIcon,
  title: 'Your students are all at different levels',
  text: 'A 6-year-old beginner at 4 pm, a teen at 5 pm and an adult returner at 6 pm?'
}, {
  icon: MusicIcon,
  title: 'Practice doesn’t happen at home',
  text: 'Do you need a simple system that tells students exactly what to practice this week?'
}, {
  icon: MessageSquareIcon,
  title: 'Parents want to see progress',
  text: 'Would you like clear reports and comments that show families how far their child has come?'
}, {
  icon: LayoutGridIcon,
  title: 'You need a clearer structure',
  text: 'Would you like everything in one place, so you find the right lesson in minutes?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  level: 'Ages 5+',
  title: 'Studio Year Plan & 12 Ready-to-Teach Lessons',
  tagline: 'Know exactly what to teach every week of the studio year.',
  intro: 'The first volume gives you a complete studio year plan and twelve lessons you can teach right away.',
  items: ['36-week studio year plan', '3 tracks: Young Beginners 5–8 · Older Beginners 9–14 · Teens & Adults', '12 lessons planned minute by minute', '30, 45 and 60-minute lesson formats', 'Materials list and teacher script for each lesson', 'Lesson-note template and progression table', 'Test A/B with answer key and assessment matrix'],
  outro: 'Ready-to-teach lessons, so you walk into every lesson with a clear plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  level: 'Ages 5+',
  title: 'The Leveled Method: Pre-Staff to Late Elementary',
  tagline: 'A clear path from the very first lesson.',
  intro: 'The second volume maps six levels and shows you what to teach at each one – and how to check it.',
  items: ['6-level map: Pre-Staff · Primer · Level 1 · Level 2 · Level 3 · Late Elementary', 'Posture and hand shape', 'Finger numbers and keyboard geography', '5-finger positions', 'First scales and chords', 'Pedal basics', 'Technique checklist per level', 'Worksheets in 3 levels'],
  outro: 'A simple, consistent method for every student who walks into your studio.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 03',
  level: 'Ages 5+',
  title: 'Note Reading & Rhythm Step by Step',
  tagline: 'Students who read music – not just copy your hands.',
  intro: 'The third volume teaches note reading and rhythm in small, logical steps with games and quick drills.',
  items: ['Landmark notes', 'Intervals: 2nds to 5ths', 'Grand staff and ledger lines', '5-minute sight-reading drills', 'Rhythm counting: quarter → dotted → eighths → triplets', 'Clapping games', 'Flashcards to print', 'Worksheets in 3 levels + answer keys'],
  outro: 'Reading and rhythm that finally stick, week after week.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 04',
  level: 'Ages 5+',
  title: 'Music Theory Without the Boredom',
  tagline: 'Theory your students actually understand – and use at the piano.',
  intro: 'The fourth volume covers the theory every piano student needs, with short activities and quizzes.',
  items: ['Key signatures', 'Major and minor scales', 'Intervals', 'Triads and inversions', 'I–IV–V and cadences', 'Circle of fifths', 'Ear training', '3-level worksheets, quizzes + answer keys'],
  outro: 'Theory connected to the music on the stand, not a separate chore.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 05',
  level: 'Ages 5+',
  title: 'Repertoire, Recitals & Creativity',
  tagline: 'The right piece, at the right level, at the right time.',
  intro: 'The fifth volume guides your repertoire choices and adds improvisation, composition and recital planning.',
  items: ['Public-domain repertoire guide by level', 'How to teach a new piece in 4 steps', 'Practice plan per piece', 'Improvisation and composition prompts', 'Lead-sheet chords', 'Recital planning', 'Managing stage nerves'],
  outro: 'Music students love to play – and a recital everyone looks forward to.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 06',
  level: 'Ages 5+',
  title: 'Assessment, Progress Reports & Studio Policies',
  tagline: 'Show students and parents what they can do – and what comes next.',
  intro: 'The sixth volume gives you the tools to measure progress fairly and communicate it clearly.',
  items: ['Level-up criteria', 'Test A/B per level', 'Rubrics', 'Term progress reports', '100 ready-made lesson & report comments', 'Studio policy template', 'End-of-year review'],
  outro: 'Clear, professional reporting that parents notice.',
  note: 'PIANO READY™ is an independent teaching resource. It is not affiliated with or endorsed by any exam board or teachers’ association. All music is public domain or original.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digital volumes for your piano studio',
  text: 'A complete teaching system that helps you plan and teach every student, all year long. Each volume has 40 pages of ready-to-teach lessons, worksheets and material for the piano bench.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lessons planned minute by minute',
  text: 'No more blank pages. Clear plans with timings, materials and a teacher script get you started fast.'
}, {
  icon: FileTextIcon,
  title: 'Worksheets in 3 levels',
  text: 'The same concept in three levels, so every student works on it – at their own level.'
}, {
  icon: MusicIcon,
  title: 'Reading, rhythm & theory',
  text: 'Landmark notes, rhythm counting, scales, chords and cadences, explained step by step.'
}, {
  icon: LayersIcon,
  title: 'A leveled method',
  text: 'Six levels from Pre-Staff to Late Elementary, with a technique checklist for each one.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Assessment & reports',
  text: 'Rubrics, Test A/B, term progress reports and 100 ready-made comments with answer keys.'
}, {
  icon: CalendarCheckIcon,
  title: 'Studio year plan',
  text: 'A 36-week plan for three age tracks, so you always know what comes next.'
}];
export const pageTypes = ['Ready-to-teach lessons', 'Worksheets in 3 levels', 'Answer keys', 'Studio year plan'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'First Lesson Perfect Kit',
  text: 'Everything you need to start a new student with confidence – from the first call to the third lesson.',
  items: ['New-student interview', 'Placement test', 'First 3 lessons for each age group', 'Ready to print'],
  benefit: 'Every new student starts on the right foot.'
}, {
  title: 'The Home Practice System',
  text: 'Simple tools that tell students exactly what to practice – and make them want to do it.',
  items: ['Practice trackers', 'Sticker charts', 'Weekly assignment sheets', '30-day challenges'],
  benefit: 'Students who come back having practiced.'
}, {
  title: 'Parent Communication Toolkit',
  text: 'Ready-made words for every conversation with families, from the welcome to the progress update.',
  items: ['Welcome letter', 'Studio policy', '40 email & text templates', 'Progress-update scripts'],
  benefit: 'Professional communication in minutes.'
}, {
  title: 'Anti-Cancellation & Make-Up Lesson Kit',
  text: 'Policies and plans that protect your schedule and keep students learning when life gets in the way.',
  items: ['Cancellation policies', 'Make-up lesson plans', 'Online-lesson plans', '12 off-bench theory lessons'],
  benefit: 'Fewer lost lessons – and less stress.'
}, {
  title: 'Studio Class & Group Games',
  text: 'Fun, ready-to-run activities for group lessons and studio classes.',
  items: ['30 group-lesson games', 'Studio-class plans', 'Theory games', 'Ready to print'],
  benefit: 'Group classes your students look forward to.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Open PIANO READY™',
  text: 'Download the package and open the volume that fits your student.'
}, {
  title: 'Pick the level and lesson',
  text: 'Find the right lesson, worksheet or piece for the student in front of you.'
}, {
  title: 'Prepare in 5 minutes',
  text: 'Read the teacher script and materials list and get what you need ready.'
}, {
  title: 'Print and teach',
  text: 'Print the worksheets or use them on your tablet – and start the lesson with a clear plan.'
}];
export const audienceItems = ['Teach private piano lessons in your own studio', 'Teach young beginners, teens and adults', 'Want a ready-made studio year plan', 'Have students at many different levels', 'Are tired of planning every lesson from scratch', 'Want clear worksheets with answer keys', 'Want easier, clearer progress reports for parents', 'Want your Sunday nights back'];
export const timeValueTasks = ['Searching online for worksheets', 'Planning each student’s lesson', 'Making flashcards and practice charts', 'Choosing pieces by level', 'Writing progress reports'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digital volumes – 240 pages',
  detail: 'Studio year plan, leveled method, note reading, theory, repertoire and assessment'
}, {
  label: '5 bonuses at no extra cost',
  detail: 'First Lesson Kit, Home Practice System, Parent Toolkit, Make-Up Lesson Kit and Group Games'
}, {
  label: 'Ages 5 to adult',
  detail: 'Ready-to-teach lessons, worksheets in 3 levels and answer keys'
}];
export const offerIncludes = ['Instant digital download', 'PDF files you can keep', 'Print-ready', 'No physical product is shipped'];
export const PRICE = '$19';
export const PRICE_BEFORE = '$97';

// Only add authentic reviews of PIANO READY. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'How do I get the material?',
  a: 'PIANO READY™ is delivered digitally. Right after your purchase you get access to the PDF files, which you can download and keep. Use them on your computer, tablet or phone and print only the pages you need.'
}, {
  q: 'Which ages and levels does it cover?',
  a: 'Ages 5 to adult, in three tracks: Young Beginners (5–8), Older Beginners (9–14) and Teens & Adults. The leveled method goes from Pre-Staff to Late Elementary.'
}, {
  q: 'Is it tied to a specific method book or exam board?',
  a: 'No. PIANO READY™ is an independent teaching resource and works alongside any method you already use. It is not affiliated with any exam board or teachers’ association.'
}, {
  q: 'What music is included?',
  a: 'Only public-domain repertoire (such as Bach, Clementi, Czerny, Burgmüller, Schumann, Tchaikovsky, Gurlitt, Türk, Diabelli and folk songs) and original pieces. No copyrighted sheet music.'
}, {
  q: 'Can I really prepare a lesson in 5 minutes?',
  a: 'PIANO READY™ is designed to make preparation much faster. You open a volume, pick a ready-to-teach lesson and read through it in about five minutes. Time can vary with the lesson, the student and how much you adapt it.'
}, {
  q: 'Does it cover a whole studio year?',
  a: 'Yes. Volume 1 includes a 36-week studio year plan for three age tracks and 12 lessons planned minute by minute, and the other volumes give you material for every week. You choose, combine and adapt it to each student.'
}, {
  q: 'Are there worksheets in different levels?',
  a: 'Yes. Worksheets come in 3 levels, so students at different stages can work on the same concept. Answer keys are included.'
}, {
  q: 'Is assessment included?',
  a: 'Yes. Volume 6 includes level-up criteria, Test A/B per level, rubrics, term progress reports, 100 ready-made lesson and report comments, a studio policy template and an end-of-year review.'
}, {
  q: 'Do I have to print everything?',
  a: 'No. You choose which pages to print, and you can use the rest on your tablet or computer.'
}, {
  q: 'Are the five bonuses included?',
  a: `Yes. All five bonuses are included at no extra cost – everything for ${PRICE}.`
}, {
  q: 'Is this a subscription?',
  a: 'No. You pay once and get access to the digital material. No recurring charges.'
}, {
  q: 'Can I use it on my phone or tablet?',
  a: 'Yes. The PDF files open on computers, tablets and phones. A tablet or computer is best for viewing worksheets before printing.'
}, {
  q: 'Is there a guarantee?',
  a: 'Yes. PIANO READY™ comes with a 7-day money-back guarantee. If the material doesn’t meet your expectations, contact us within 7 days of your purchase and ask for a refund, according to the guarantee terms.'
}];
export const footerLinks = ['Terms of purchase', 'Privacy', 'Refunds', 'Contact'];
